// TODO: Remove this eslint-disable once the error is fixed
/* eslint-disable @typescript-eslint/no-unused-vars */

import { useEffect, useState } from 'react';
import {
  CreateRecordingProps,
  UpdateRecordingProps,
  Recording,
  RecordingId,
  ISRC,
  AuthorComposer,
  Cradle,
  RecordingType,
  Contributor,
  FileEntity,
  FileId,
  Language,
  ContributorRole,
  TrackProperties,
  MusicalRelease,
} from '@solo/app-core';
import { toast } from 'react-toastify';
import { useParams } from 'react-router-dom';
import { translate } from '@/app/i18n';
import { Button, Flex, Text } from '@/app/ui/atoms';
import {
  CheckboxWithLabel,
  Icon,
  InputTextAutocomplete,
  InputWithLabel,
  Modal,
  RadioButtonWithLabel,
  TextAreaWithLabel,
} from '@/app/ui/molecules';
import {
  ArtistsTableComponent,
  AuthorsComposersTableComponent,
  AuthorsNameTableComponent,
  FileUploadComponent,
  RecordingAuthorComposerPopUp,
} from '@/app/ui/organisms';
import { useCradle } from '@/app/contexts';
import { useBlocState } from '@/app/hooks';
import { artistContributorRoles, composerContributorRoles } from '@/app/utils/revelatorPayload';

interface RecordingFormProps {
  closeForm: () => void;
  recordingData?: Recording;
  createRecording: (recording: CreateRecordingProps) => Promise<void>;
  getAudioInfo: (audioId: FileId) => Promise<FileEntity>;
  updateRecording: (recording: UpdateRecordingProps, recordingId: RecordingId) => Promise<void>;
  setNewAudio: (audio: File) => void;
  musicalRelease?: MusicalRelease;
  existingTracksCount?: number;
}

const trackProperties = Object.values(TrackProperties || {})
  .filter((right) => typeof right === 'number')
  .map((right) => ({
    label: translate(`TRACK_PROPERTIES.${right}`),
    value: right.toString(),
  }));

const RecordingForm = (props: RecordingFormProps) => {
  const {
    closeForm,
    recordingData,
    createRecording,
    updateRecording,
    setNewAudio,
    getAudioInfo,
    musicalRelease,
    existingTracksCount,
  } = props;
  const { authBloc, revelatorDataBloc } = useCradle<Cradle>();
  const revelatorDataState = useBlocState(revelatorDataBloc);
  const [recordingFormData, setRecordingFormData] = useState<
    CreateRecordingProps | UpdateRecordingProps
  >({
    ...recordingData,
    explicit: recordingData?.explicitContent || false,
    name: recordingData?.title,
    audioFileId: recordingData?.audioId,
    languageId: recordingData?.languageId,
  });

  const [fieldsMissing, setFieldsMissing] = useState<boolean>(false);
  const [originalAuthors, setOriginalAuthors] = useState<string[] | undefined>();
  const [recordingAuthors, setRecordingAuthors] = useState<AuthorComposer[]>(
    recordingData?.authors || [],
  );
  const [addingAuthorComposer, setAddingAuthorComposer] = useState<boolean>(false);
  const [otherArtist, setOtherArtist] = useState<string>();
  const [otherArtistRole, setOtherArtistRole] = useState<number>();
  const [hasIsrc, setHasIsrc] = useState<boolean | undefined>(recordingData?.isrc !== undefined);
  const [isrc, setIsrc] = useState<ISRC | undefined>();
  const [audio, setAudio] = useState<File | undefined>();
  const [audioInfo, setAudioInfo] = useState<FileEntity | undefined>();
  const [loading, setLoading] = useState<boolean>(false);
  const [languages, setLanguages] = useState<Language[]>([]);
  const [roles, setRoles] = useState<ContributorRole[]>([]);
  const { id: releaseId } = useParams();

  useEffect(() => {
    if (recordingData && recordingData.audioId) {
      getAudioInfo(recordingData.audioId).then(setAudioInfo);
    }
  }, []);

  useEffect(() => {
    if (audio) setNewAudio(audio);
  }, [audio]);

  function deleteOriginalAuthor(author: string) {
    setOriginalAuthors(originalAuthors?.filter((currentAuthor) => currentAuthor !== author));
  }

  function deleteRecordingAuthor(recordingAuthor: AuthorComposer) {
    setRecordingAuthors(
      recordingAuthors?.filter(
        (currentrecordingAuthor) => currentrecordingAuthor !== recordingAuthor,
      ),
    );
  }

  function updateRecordingAuthors(recordingAuthor: AuthorComposer) {
    const existingRecordingAuthorIndex = recordingAuthors.findIndex(
      (ra) => ra.authorComposerId === recordingAuthor.authorComposerId,
    );

    if (existingRecordingAuthorIndex === undefined) return;
    const auxRecordingAuthors = [...recordingAuthors];
    auxRecordingAuthors[existingRecordingAuthorIndex] = recordingAuthor;
    setRecordingAuthors(auxRecordingAuthors);
  }

  function deleteArtist(artist: Contributor) {
    const artistIndex = recordingFormData.contributors?.findIndex(
      (a) => a.artist.name === artist.artist.name,
    );
    if (artistIndex === undefined || !recordingFormData.contributors) return;
    let artists: Contributor[] | undefined;
    if (recordingFormData.contributors.length > 1) {
      artists = recordingFormData.contributors?.splice(artistIndex - 1, 1);
    }
    setRecordingFormData({
      ...recordingFormData,
      contributors: artists,
    });
  }

  function addOtherArtist() {
    if (!recordingFormData || !otherArtist || otherArtistRole === undefined) return;
    let otherArtists = recordingFormData.contributors;
    const newArtist = { artist: { name: otherArtist }, roleId: otherArtistRole };
    if (!otherArtists) {
      otherArtists = [newArtist];
    } else {
      otherArtists.push(newArtist);
    }
    setRecordingFormData({ ...recordingFormData, contributors: otherArtists });
    setOtherArtist(undefined);
  }

  function checkMandatory() {
    if (
      !recordingFormData.name ||
      recordingFormData.explicit === undefined ||
      !recordingFormData.artistName ||
      recordingAuthors.length === 0 ||
      (audio === undefined && !recordingFormData.audioFileId) ||
      recordingFormData.trackProperties?.length === 0
    ) {
      return false;
    }

    return true;
  }

  function clearForm() {
    setRecordingFormData({});
    setRecordingAuthors([]);
    setOriginalAuthors([]);
    setOtherArtist(undefined);
    setOtherArtistRole(undefined);
    setAudio(undefined);
    setAudioInfo(undefined);
  }

  async function saveData() {
    if (authBloc.state.kind !== 'AuthenticatedAuthState') return;
    if (loading) return;

    const releaseIdToUse = releaseId || musicalRelease?.id;

    if (!checkMandatory() || !releaseIdToUse) {
      setFieldsMissing(true);
      throw new Error('All mandatory fields must be filled!');
    }

    if (
      recordingAuthors
        .filter((author) => author.mechanicalSplit)
        .reduce((perf, cur) => cur.mechanicalSplit || 0 + perf, 0) > 100
    ) {
      throw new Error('MECH_100_ERROR');
    }

    const nextOrderPosition = (musicalRelease?.recordings?.length ?? existingTracksCount ?? 0) + 1;
    const existingOrderPosition = recordingFormData.orderPosition || recordingData?.orderPosition;
    let orderPosition = nextOrderPosition;
    if (existingOrderPosition && existingOrderPosition > 0) {
      orderPosition = existingOrderPosition;
    } else if (recordingData && musicalRelease?.recordings) {
      orderPosition = Math.max(
        musicalRelease.recordings.findIndex((track) => track.id === recordingData.id) + 1,
        1,
      );
    }

    const data: CreateRecordingProps = {
      ...recordingFormData,
      orderPosition,
      authors: recordingAuthors,
      isrc: isrc || null,
      userId: authBloc.state.user.id,
      name: recordingFormData.name || '',
      trackType: RecordingType.ORIGINAL ? 1 : 3,
      explicit: recordingFormData.explicit || false,
      lyrics: recordingFormData.lyrics || '',
      audioFileId: recordingFormData.audioFileId || '',
      artistName: recordingFormData.artistName || '',
      tracksLocals: [],
      artistLocals: [],
      contributors: recordingFormData?.contributors || [],
      artistAppleId: '',
      artistSpotifyId: '',
      languageId: recordingFormData.languageId || 1,
      releaseId: releaseIdToUse,
      trackProperties: recordingFormData.trackProperties || [],
    };

    setLoading(true);

    try {
      if (recordingData) {
        await updateRecording(data, recordingData.id);
      } else {
        await createRecording(data);
      }
    } finally {
      setLoading(false);
    }
  }

  async function saveDataAndClose() {
    try {
      await saveData();
      clearForm();
      closeForm();
    } catch (error) {
      const errorMessage = error as Error;
      if (errorMessage.message === 'MECH_100_ERROR') {
        toast.error(translate('TOAST.MECH_100_ERROR'));
      } else {
        toast.error(translate('TOAST.MANDATORY_ERROR'));
      }
    }
  }

  async function saveDataAndContinue() {
    try {
      await saveData();
      clearForm();
      closeForm();
    } catch (error) {
      const errorMessage = error as Error;
      if (errorMessage.message === 'MECH_100_ERROR') {
        toast.error(translate('TOAST.MECH_100_ERROR'));
      } else {
        toast.error(translate('TOAST.MANDATORY_ERROR'));
      }
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  useEffect(() => {
    if (revelatorDataState.kind === 'LoadedRevelatorDataState') {
      setRoles(revelatorDataState.contributorRoles || []);
      setLanguages(revelatorDataState.languages || []);
    }
  }, [revelatorDataState.kind]);

  return (
    <Flex.Container flexDirection="column" gap="4rem" style={{ width: '100%' }}>
      <Flex.Container gap="30px">
        <Text.SubtitleBold>
          {recordingData === undefined
            ? translate('WORKS_PAGES.NEW_TRACK')
            : translate('WORKS_PAGES.EDIT_TRACK')}
        </Text.SubtitleBold>
        <Button
          $transparent
          $variant="primary"
          onClick={() => {
            closeForm();
          }}
          disabled={loading}
        >
          <Text.LabelMedium style={{ padding: 0 }} fontWeight={600}>
            {translate('PROFILE_INFO.CANCEL')}
          </Text.LabelMedium>
        </Button>
      </Flex.Container>
      <Flex.Container gap="30px" flexDirection="column">
        <Text.SubtitleBold>{`1. ${translate('TRACKS.TRACK_TITLE')}`}</Text.SubtitleBold>
        <InputWithLabel
          placeholder={translate('TRACKS.TRACK_TITLE')}
          label={translate('TRACKS.TRACK_TITLE')}
          value={recordingFormData?.name}
          onChange={(name) => setRecordingFormData({ ...recordingFormData, name })}
          mandatoryError={fieldsMissing && !recordingFormData.name}
        />
      </Flex.Container>
      <Flex.Container gap="20px" flexDirection="column">
        <Text.SubtitleBold>{`2. ${translate('WORKS_PAGES.TRACK_DETAILS')}`}</Text.SubtitleBold>
        <Flex.Container gap="16px">
          <Flex.Container flex={1}>
            <InputTextAutocomplete
              label={translate('WORKS.LANGUAGE')}
              tooltip
              items={Array.from(languages, (language: Language) => ({
                value: language.id.toString(),
                label: language.name,
              }))}
              defaultOption={
                recordingFormData.languageId !== undefined
                  ? {
                      value: recordingFormData.languageId.toString(),
                      label:
                        languages.find((l) => l.id === recordingFormData.languageId)?.name || '',
                    }
                  : undefined
              }
              onChange={(language) =>
                setRecordingFormData({
                  ...recordingFormData,
                  languageId: languages.find((l) => l.id === Number(language))?.id || undefined,
                })
              }
            />
          </Flex.Container>
          <Flex.Container flex={1}>
            <InputTextAutocomplete
              label={translate('TRACKS.EXPLICIT_CONTENT')}
              placeholder={`${translate('TRACKS.YES')}/${translate('TRACKS.NO')}`}
              tooltip
              items={Array.from(
                [translate('TRACKS.YES'), translate('TRACKS.NO')],
                (response: string) => ({
                  value: response,
                  label: response,
                }),
              )}
              defaultOption={
                recordingFormData.explicit !== undefined
                  ? {
                      value: recordingFormData.explicit
                        ? translate('TRACKS.YES')
                        : translate('TRACKS.NO'),
                      label: recordingFormData.explicit
                        ? translate('TRACKS.YES')
                        : translate('TRACKS.NO'),
                    }
                  : undefined
              }
              onChange={(explicit) =>
                setRecordingFormData({
                  ...recordingFormData,
                  explicit: explicit === translate('TRACKS.YES'),
                })
              }
              mandatoryError={fieldsMissing && recordingFormData.explicit === undefined}
            />
          </Flex.Container>
        </Flex.Container>
        <Flex.Container flex={1}>
          <InputTextAutocomplete
            label={translate('TRACKS.PROPERTIES')}
            placeholder={translate('TRACKS.PROPERTIES')}
            items={trackProperties}
            multiple
            defaultOption={recordingFormData.trackProperties?.map((property) => ({
              value: property.toString(),
              label: translate(`TRACK_PROPERTIES.${property}`),
            }))}
            onChangeMulti={(property) =>
              setRecordingFormData({
                ...recordingFormData,
                trackProperties: property.map((p) => parseInt(p, 10) as TrackProperties),
              })
            }
            mandatoryError={fieldsMissing && recordingFormData.explicit === undefined}
          />
        </Flex.Container>
        <Flex.Container gap="25px" flexDirection="column">
          <Text.SubtitleBold>{translate('TRACKS.AUDIO')} *</Text.SubtitleBold>
          <FileUploadComponent
            type="audio"
            subtitle={audioInfo?.name}
            onChange={(audioFile) => setAudio(audioFile)}
            mandatoryError={fieldsMissing && !audio && !recordingData?.audioId}
          />
        </Flex.Container>
        <TextAreaWithLabel
          rows={5}
          height="120px"
          placeholder={translate('TRACKS.LYRICS')}
          label={translate('TRACKS.LYRICS')}
          defaultValue={recordingFormData?.lyrics || undefined}
          onChange={(lyrics: string) => setRecordingFormData({ ...recordingFormData, lyrics })}
        />
      </Flex.Container>
      <Flex.Container gap="30px" flexDirection="column">
        <Flex.Container gap="10px" alignItems="center">
          <Text.SubtitleBold>{`3. ${translate('WORKS_PAGES.ARTIST_PERFORMER')}`}</Text.SubtitleBold>
          <Icon.InfoIcon />
        </Flex.Container>

        <Flex.Container gap="16px" flexDirection="column">
          <Flex.Container flex={4}>
            <Flex.Item flex={1}>
              <InputWithLabel
                label={translate('WORKS.MAIN_ARTIST')}
                placeholder={translate('WORKS.MAIN_ARTIST')}
                onChange={(name) =>
                  setRecordingFormData({ ...recordingFormData, artistName: name })
                }
                value={recordingFormData.artistName}
                mandatoryError={fieldsMissing && !recordingFormData.artistName}
              />
            </Flex.Item>
            <Flex.Item flex={1} />
          </Flex.Container>

          <Flex.Container flex={3} gap="2rem" alignItems="flex-end">
            <Flex.Item flex={2}>
              <InputWithLabel
                placeholder={translate('WORKS.OTHER_ARTISTS')}
                label={translate('WORKS.OTHER_ARTISTS')}
                onChange={(name) => setOtherArtist(name)}
                value={otherArtist}
              />
            </Flex.Item>
            <Flex.Item flex={2}>
              <InputTextAutocomplete
                label={translate('TRACKS.ROLE')}
                placeholder={translate('WORKS_PAGES.TYPES_OF_ROLE')}
                items={Array.from(artistContributorRoles(roles), (role: ContributorRole) => ({
                  value: role.id.toString(),
                  label: role.name,
                }))}
                onChange={(role) =>
                  setOtherArtistRole(roles.find((r) => r.id === Number(role))?.id || undefined)
                }
              />
            </Flex.Item>
            <Flex.Item flex={1}>
              <Button
                $size="md"
                $variant="primary"
                style={{ height: '38px', marginTop: '21px' }}
                onClick={() => addOtherArtist()}
              >
                <Text.SectionSubtitle style={{ padding: 0 }} fontWeight={600}>
                  {translate('BUTTON.ADD_ARTIST')}
                </Text.SectionSubtitle>
              </Button>
            </Flex.Item>
          </Flex.Container>
          <ArtistsTableComponent
            artists={recordingFormData.contributors || []}
            deleteArtist={(artist) => deleteArtist(artist)}
            roles={roles}
          />
        </Flex.Container>
      </Flex.Container>
      {/* {recordingFormData.type === RecordingType.ORIGINAL ? ( */}
      <Flex.Container gap="30px" flexDirection="column">
        <Flex.Container gap="10px" alignItems="center">
          <Text.SubtitleBold>{`4. ${translate('WORKS_PAGES.AUTHOR_COMPOSERS')}`}</Text.SubtitleBold>
          <Icon.InfoIcon />
        </Flex.Container>
        <Flex.Container alignItems="center">
          <CheckboxWithLabel label={translate('WORKS_PAGES.SONG_EDITED')} tooltip />
          <Button
            $size="md"
            $variant="primary"
            style={{ height: '40px', marginLeft: 'auto' }}
            onClick={() => setAddingAuthorComposer(true)}
          >
            <Text.SectionSubtitle style={{ padding: 0 }} fontWeight={600}>
              {translate('BUTTON.ADD_AUTHOR_COMPOSER')}
            </Text.SectionSubtitle>
          </Button>
        </Flex.Container>
        {recordingAuthors && recordingAuthors?.length > 0 ? (
          <AuthorsComposersTableComponent
            recordingAuthors={recordingAuthors}
            deleteRecordingAuthor={(recordingAuthor) => deleteRecordingAuthor(recordingAuthor)}
            saveRecordingAuthor={(recordingAuthor) => updateRecordingAuthors(recordingAuthor)}
            roles={composerContributorRoles(roles)}
          />
        ) : (
          <Text.ErrorMessage>
            {fieldsMissing && translate('TRACKS.MISSING_AUTHOR')}
          </Text.ErrorMessage>
        )}
      </Flex.Container>
      {originalAuthors !== undefined && originalAuthors?.length > 0 && (
        <AuthorsNameTableComponent
          authors={originalAuthors}
          deleteAuthor={(author: string) => deleteOriginalAuthor(author)}
        />
      )}
      <Flex.Container gap="30px" flexDirection="column">
        <Text.SubtitleBold>5. ISRC</Text.SubtitleBold>
        <Flex.Container gap="20px" flexDirection="column">
          <RadioButtonWithLabel
            checked={!hasIsrc && hasIsrc !== undefined}
            label={translate('WORKS_PAGES.NEED_ISRC')}
            onChange={() => setHasIsrc(false)}
          />
          <RadioButtonWithLabel
            checked={hasIsrc || false}
            label={translate('WORKS_PAGES.HAVE_ISRC')}
            onChange={() => setHasIsrc(true)}
          />
          {hasIsrc && (
            <InputWithLabel
              placeholder="ISRC"
              label="ISRC"
              value={recordingData?.isrc}
              onChange={(i) => setIsrc(i)}
            />
          )}
        </Flex.Container>
      </Flex.Container>
      <Flex.Container style={{ marginLeft: 'auto' }} gap="30px">
        <Button
          $transparent
          $variant="primary"
          onClick={() => {
            closeForm();
          }}
          disabled={loading}
        >
          <Text.SectionSubtitle>{translate('PROFILE_INFO.CANCEL')}</Text.SectionSubtitle>
        </Button>
        <Button
          $size="sm"
          $variant="reversed"
          $transparent
          $outline
          style={{ height: '40px', width: '160px' }}
          disabled={loading}
          onClick={() => {
            saveDataAndClose();
          }}
        >
          <Text.SectionSubtitle style={{ padding: 0 }} fontWeight={600}>
            {translate('PROFILE_INFO.SAVE')}
          </Text.SectionSubtitle>
        </Button>
        {!recordingData && (
          <Button
            $size="md"
            $variant="primary"
            style={{ width: '290px' }}
            disabled={loading}
            onClick={() => {
              saveDataAndContinue();
            }}
          >
            <Text.SectionSubtitle style={{ padding: 0 }} fontWeight={600}>
              {translate('BUTTON.SAVE_ADD_TRACK')}
            </Text.SectionSubtitle>
          </Button>
        )}
      </Flex.Container>
      <Modal
        isOpen={addingAuthorComposer}
        onClose={() => setAddingAuthorComposer(false)}
        closeable
        backdropCloseable
        position="top"
        size="md"
      >
        <RecordingAuthorComposerPopUp
          currentRecordingAuthors={recordingAuthors}
          closeModal={() => setAddingAuthorComposer(false)}
          saveRecordingAuthor={(recordingAuthor: AuthorComposer) => {
            setRecordingAuthors([...recordingAuthors, recordingAuthor]);
          }}
          roles={composerContributorRoles(roles)}
        />
      </Modal>
    </Flex.Container>
  );
};

export default RecordingForm;
